import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Textarea,
} from '@roger/ui-components';
import { HelpCircle, Loader2 } from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { useToast } from '../hooks/useToastContext';
import { useSupportContext, useSupportModal } from '../hooks/useSupportContext';
import { SUPPORT_RECIPIENT_EMAILS } from '../contexts/supportContextValue';
import { sendSupportEmailApiSupportContactPost } from '../services/api/sdk.gen';

const SupportButton: React.FC = () => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { isOpen, openSupport, closeSupport } = useSupportModal();

  const { user } = useStore(
    useShallow(state => ({
      user: state.user,
    }))
  );

  const { success, error: showError } = useToast();
  const supportContext = useSupportContext();
  const recipient = supportContext.recipient ?? 'support';
  const recipientEmail = SUPPORT_RECIPIENT_EMAILS[recipient];

  const label = supportContext.label ? (
    <>Describe the issue for {supportContext.label}</>
  ) : (
    'Describe your issue'
  );

  const handleSend = async () => {
    if (!message.trim()) return;

    setSending(true);
    try {
      await sendSupportEmailApiSupportContactPost({
        body: {
          subject: supportContext.subject || 'Support request',
          message: message.trim(),
          recipient,
          hidden_context: {
            url: window.location.href,
            ...(user?.agency?.written_name && {
              agency: user.agency.written_name,
            }),
            ...supportContext.hiddenContext,
          },
        },
      });
      success('Support request sent', 'Our team will get back to you shortly.');
      setMessage('');
      closeSupport();
    } catch (e) {
      console.error('Failed to send support email:', e);
      showError('Failed to send support request', 'Please try again later.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='lg'
        onClick={() => openSupport()}
        aria-label='Contact Support'
      >
        <HelpCircle aria-hidden='true' />
        <span className='hidden sm:inline'>Contact Support</span>
      </Button>

      <Dialog
        open={isOpen}
        onOpenChange={open => {
          if (!open) closeSupport();
        }}
      >
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Contact Support</DialogTitle>
          </DialogHeader>

          <div className='space-y-4'>
            <div className='rounded-md border-l-4 border-primary bg-muted/50 px-4 py-3 text-sm'>
              Sending to: {recipientEmail}
            </div>

            <div>
              <p className='mb-1 text-sm font-medium'>Your email</p>
              <p className='rounded-md bg-muted px-3 py-2 text-sm'>
                {user?.email || '—'}
              </p>
            </div>

            <div>
              <label
                htmlFor='support-message'
                className='mb-1 block text-sm font-medium'
              >
                {label}
              </label>
              <Textarea
                id='support-message'
                placeholder='Please be as specific as possible.'
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={5}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={closeSupport} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending || !message.trim()}>
              {sending ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Sending...
                </>
              ) : (
                'Send'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupportButton;
